import type { SxProps, Theme } from "@mui/material/styles";
import * as React from "react";

export function useClampOverflow(
  text: string | null | undefined,
  isExpanded: boolean,
  lineCount: number,
): {
  ref: React.RefObject<HTMLSpanElement>;
  clampSx: SxProps<Theme>;
  showToggle: boolean;
  hasText: boolean;
} {
  const [showToggle, setShowToggle] = React.useState(false);
  const ref = React.useRef<HTMLSpanElement>(
    null,
  ) as React.RefObject<HTMLSpanElement>;

  const hasText = typeof text === "string" && text.trim().length > 0;

  const recompute = React.useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!ref.current || !hasText) {
      setShowToggle(false);

      return;
    }
    // quick guard for very short strings
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const likelyLong = (text?.length ?? 0) > 120;

    if (!isExpanded) {
      const el = ref.current;
      const overflows = el.scrollHeight > el.clientHeight + 1;
      setShowToggle(likelyLong && overflows);
    } else {
      setShowToggle(true);
    }
  }, [hasText, isExpanded, text]);

  React.useLayoutEffect(() => {
    recompute();
  }, [recompute, lineCount]);

  React.useEffect(() => {
    const el = ref.current;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!el) return;
    const ro = new ResizeObserver(recompute);
    ro.observe(el);

    return () => ro.disconnect();
  }, [recompute]);

  const clampSx: SxProps<Theme> = isExpanded
    ? {}
    : {
        WebkitLineClamp: lineCount,
        overflow: "hidden",
      };

  return { ref, clampSx, showToggle, hasText };
}
